import { ChangeEvent, useState, useMemo } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { AXNUILayout, AXNUIContent, AXNUIContentSuccess } from "@iddataweb/idw-common-ui/types";
import { ThemingSuccessPageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { RadioInputFormGroup } from "../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingSuccessPageTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingSuccessPageValidationErrors;
    axnuiContent: AXNUIContent,
    axnuiLayout: AXNUILayout;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
    pageLevel: PageLevelTypes;
};

export const ThemingSuccessPageTabContainer = (props: ThemingSuccessPageTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        axnuiLayout,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
        updateAXNUILayoutPropertyValue,
        pageLevel
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIContentSuccessFieldValue = (key: keyof AXNUIContentSuccess, value: string | boolean) => {
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            success: {
                ...axnuiContent.success,
                [key]: value
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const handleShowSuccessPageChange = (newValue: string) => {
        updateAXNUILayoutPropertyValue({
            ...axnuiLayout,
            isSuccessPageEnabled: isStringTrue(newValue)
        });
    };

    const handleResetShowSucessPage = () => {
        updateAXNUILayoutPropertyValue({
            ...axnuiLayout,
            isSuccessPageEnabled: inheritedAXNUILayout.isSuccessPageEnabled
        })
    };

    const handleSuccessPageHeaderChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentSuccessFieldValue("successTitle", event.target.value);
    };

    const handleResetSuccessPageHeader = () => {
        updateAXNUIContentSuccessFieldValue("successTitle", inheritedAXNUIContent.success.successTitle);
    };

    const handleSuccessTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentSuccessFieldValue("message", event.target.value);
    };

    const handleResetSuccessText = () => {
        updateAXNUIContentSuccessFieldValue("message", inheritedAXNUIContent.success.message);
    };

    const handleShowLoadingIndicatorChange = (newValue: string) => {
        updateAXNUIContentSuccessFieldValue("showLoadingIndicator", isStringTrue(newValue));
    };

    const handleResetShowLoadingIndicator = () => {
        updateAXNUIContentSuccessFieldValue("showLoadingIndicator", inheritedAXNUIContent.success.showLoadingIndicator);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.SUCCESS_PAGE);
    }, [generatePreviewComponent]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection showSeparator={axnuiLayout.isSuccessPageEnabled}>
                            <ThemingFormGroupWrapper
                                pageLevel={pageLevel}
                                inheritedThemingPropertyValue={inheritedAXNUILayout.isSuccessPageEnabled}
                                currentThemingPropertyValue={axnuiLayout.isSuccessPageEnabled}
                            >
                                <RadioInputFormGroup
                                    controlId={`theming-success-page-radio-input`}
                                    automationId={`theming-success-page-radio-input`}
                                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.showSuccessPage.label}
                                    layout={layout}
                                    labelWidth={labelWidth}
                                    onChange={handleShowSuccessPageChange}
                                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                    value={axnuiLayout.isSuccessPageEnabled ? "true" : "false"}
                                    disabled={isDisabled}
                                    isRequired={true}
                                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.showSuccessPage.tooltip}
                                    afterLabelIcon={isDisabled ? null : faUndo}
                                    afterLabelIconOnClick={handleResetShowSucessPage}
                                />
                            </ThemingFormGroupWrapper>
                        </ThemingFormSection>
                        {axnuiLayout.isSuccessPageEnabled && (
                            <ThemingFormSection>
                                <ThemingFormGroupWrapper
                                    pageLevel={pageLevel}
                                    inheritedThemingPropertyValue={inheritedAXNUIContent.success.successTitle}
                                    currentThemingPropertyValue={axnuiContent.success.successTitle}
                                >
                                    <InputFormGroup
                                        controlId={"theming-success-page-header-input"}
                                        automationId={"theming-success-page-header-input"}
                                        label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.successPageHeader.label}
                                        ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.successPageHeader.label}
                                        layout={layout}
                                        labelWidth={labelWidth}
                                        onChange={handleSuccessPageHeaderChange}
                                        value={axnuiContent.success.successTitle || ""}
                                        disabled={isDisabled}
                                        isRequired={true}
                                        afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.successPageHeader.tooltip}
                                        afterLabelIcon={isDisabled ? null : faUndo}
                                        afterLabelIconOnClick={handleResetSuccessPageHeader}
                                    />
                                </ThemingFormGroupWrapper>
                                {validationErrors.successTitleError}
                                <ThemingFormGroupWrapper
                                    pageLevel={pageLevel}
                                    inheritedThemingPropertyValue={inheritedAXNUIContent.success.message}
                                    currentThemingPropertyValue={axnuiContent.success.message}
                                >
                                    <TextareaFormGroup
                                        controlId={"theming-success-page-success-text-input"}
                                        automationId={"theming-success-page-success-text-input"}
                                        label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.successText.label}
                                        ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.successText.label}
                                        layout={layout}
                                        labelWidth={labelWidth}
                                        onChange={handleSuccessTextChange}
                                        value={axnuiContent.success.message || ""}
                                        disabled={isDisabled}
                                        isRequired={false}
                                        afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.successText.tooltip}
                                        afterLabelIcon={isDisabled ? null : faUndo}
                                        afterLabelIconOnClick={handleResetSuccessText}
                                    />
                                </ThemingFormGroupWrapper>

                                <ThemingFormGroupWrapper
                                    pageLevel={pageLevel}
                                    inheritedThemingPropertyValue={inheritedAXNUIContent.success.showLoadingIndicator}
                                    currentThemingPropertyValue={axnuiContent.success.showLoadingIndicator}
                                >
                                    <RadioInputFormGroup
                                        controlId={`theming-success-page-show-loading-indicator-radio-input`}
                                        automationId={`theming-success-page-show-loading-indicator-radio-input`}
                                        label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.showLoadingIndicator.label}
                                        layout={layout}
                                        labelWidth={labelWidth}
                                        onChange={handleShowLoadingIndicatorChange}
                                        options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                        value={axnuiContent.success.showLoadingIndicator ? "true" : "false"}
                                        disabled={isDisabled}
                                        isRequired={true}
                                        afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.successPage.inputs.showLoadingIndicator.tooltip}
                                        afterLabelIcon={isDisabled ? null : faUndo}
                                        afterLabelIconOnClick={handleResetShowLoadingIndicator}
                                    />
                                </ThemingFormGroupWrapper>
                            </ThemingFormSection>
                        )}
                    </ThemingFormContainer>
                    {axnuiLayout.isSuccessPageEnabled && (previewComponent)}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div >
    );
};